import { useMemo, useEffect } from 'react';

import type Search from 'views/logic/search/Search';
import useUserDateTime from 'hooks/useUserDateTime';
import { onInitializingTimerange } from 'views/components/TimerangeForForm';
import { WIDGET_DEFAULTS } from 'data-lake/preview/Constants';
import type { ExternalDataLakeSearchType } from 'data-lake/preview/storage-types/external/Types';
import type { DataLakeURLQueryParams } from 'data-lake/preview/Types';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';

const useInitialSearchBarFormValues = (executedSearch: Search, initialUrlParams: DataLakeURLQueryParams) => {
  const { formatTime } = useUserDateTime();
  const { setDataLakeFieldsFetchQuery } = useSelectedDataLake();

  const initialValues = useMemo(() => {
    if (executedSearch) {
      const searchType = executedSearch.queries.first().searchTypes[0] as ExternalDataLakeSearchType;

      return {
        table: searchType.table_id,
        timerange: onInitializingTimerange(searchType.timerange, formatTime),
        fields: {
          fieldFilters: searchType.field_filters ?? WIDGET_DEFAULTS.fieldFilters,
          operator: searchType.filters_operator ?? WIDGET_DEFAULTS.fieldsOperator,
        },
      };
    }

    return {
      table: initialUrlParams.table,
      timerange: initialUrlParams.timerange ?? WIDGET_DEFAULTS.timerange,
      fields: {
        fieldFilters: initialUrlParams.fields?.fieldFilters ?? WIDGET_DEFAULTS.fieldFilters,
        operator: initialUrlParams.fields?.operator ?? WIDGET_DEFAULTS.fieldsOperator,
      },
    };
  }, [executedSearch, formatTime, initialUrlParams.fields, initialUrlParams.table, initialUrlParams.timerange]);

  useEffect(() => {
    if (initialValues?.table) {
      setDataLakeFieldsFetchQuery((cur) => ({ ...cur, tableId: initialValues.table }));
    }
  }, [initialValues.table, setDataLakeFieldsFetchQuery]);

  return initialValues;
};

export default useInitialSearchBarFormValues;
